import { Grid, IconButton } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { Link, Typography } from '@uhc-tempo/components';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { FormContext } from '../../../context/Form.context';
import { useHistory } from 'react-router-dom';
import { ERROR_ROUTE, ESTIMATE_HISTORY_TYPES } from '../../../constants';
import { getPrice } from '../../../API/PriceService';
import { getPrimarySelectedService } from '../../../utils/carePathUtil';
import CarePathService from '../../../API/CarePathService.js';
import GenericModal from '../../../common/components/GenericModal.component';
import { modalStyles } from '../../../styles/GenericModalComponentStyles';
import displayLoadingIndicator from '../../../common/components/LoadingIndicator.components';

export default function EstimateToggleComponent(props) {
    const {
        isPreventive,
        setIsPreventive,
        estimateParameters,
        allProvidersPool,
        sourceSystem,
        planBenefits,
        getIncrementedCorId,
        handleChangeCustom,
        memberDetails,
        formValues,
        setAllProvidersPool,
        setEstimateResponse,
        setSingleEstimateResponse,
        estimatesHistory,
    } = useContext(FormContext);
    const [showPreventiveModal, setShowPreventiveModal] = React.useState(false);
    const {
        setLoading,
        setProvider,
        setCurrentSelectedServiceForDisplay,
        loading,
        provider,
        toggleType,
        serviceInfo,
    } = props;
    const history = useHistory();

    const [relatedCarePathsData, setRelatedCarePathsData] = useState({
        [ESTIMATE_HISTORY_TYPES.rental]: null,
        [ESTIMATE_HISTORY_TYPES.purchase]: null,
    });

    useEffect(() => {
        if (serviceInfo) {
            fetchRelatedCarepathsData();
        }
    }, []);

    let nextIsPreventiveState = isPreventive;

    const { selectedProvider, selectedService } = formValues;
    const { allProviders, oon, gacOnly } = estimateParameters;
    const hasPreventive = selectedService?.value?.fullCarePath?.preventive;
    const isEstimateScreen = true;

    const preventiveVerbiage = () => {
        return (
            <div>
                <p>
                    <b> Preventive Care </b> is a routine check.
                </p>
                <p>
                    <b> Diagnostic Care </b> may be suggested when you have risk factors or symptoms
                    and your doctor wants to diagnose them.
                </p>
            </div>
        );
    };

    /**
     * Function to handle the toggle in UI for preventive/Diagnostic estimate and rental/purchase estimate.
     */
    const handleEstimateToggle = () => {
        if (toggleType == 'PreventiveAndDiagnostic') {
            handlePreventiveDiagnosticToggle();
        } else {
            handleRentalAndPurchaseToggle();
        }
    };

    const fetchRelatedCarepathsData = async () => {
        const modifierValue = serviceInfo?.lineItem?.codeModifier;
        let matchingCarepath = null;
        let primarySelectedService = null;
        const relatedCarePaths = {};

        if (
            serviceInfo.fullCarePath?.relatedCarePaths &&
            serviceInfo.fullCarePath.relatedCarePaths.length != 0 &&
            matchingCarepath == null &&
            [ESTIMATE_HISTORY_TYPES.rental, ESTIMATE_HISTORY_TYPES.purchase].includes(modifierValue)
        ) {
            const relatedCarePath = {
                carePathId: serviceInfo.fullCarePath?.relatedCarePaths[0],
                carePathCategoryCode: serviceInfo.fullCarePath?.coverageTypeCode,
            };

            let relatedCarePathResponse;

            try {
                relatedCarePathResponse = await CarePathService.getCarepathDataResponse(
                    memberDetails,
                    relatedCarePath,
                    getIncrementedCorId()
                );
            } catch (error) {
                console.error(`Error while fetching related DME carepath data: ${error}`);
                history.push(ERROR_ROUTE);
            }

            if (relatedCarePathResponse) {
                primarySelectedService = getPrimarySelectedService(relatedCarePathResponse);
            }
        }

        if (modifierValue == ESTIMATE_HISTORY_TYPES.rental) {
            relatedCarePaths[ESTIMATE_HISTORY_TYPES.rental] = serviceInfo;
            relatedCarePaths[ESTIMATE_HISTORY_TYPES.purchase] = primarySelectedService;
        } else {
            relatedCarePaths[ESTIMATE_HISTORY_TYPES.rental] = primarySelectedService;
            relatedCarePaths[ESTIMATE_HISTORY_TYPES.purchase] = serviceInfo;
        }

        setRelatedCarePathsData(relatedCarePaths);
    };

    /**
     * Function to rental/purchase toggle.
     */
    const handleRentalAndPurchaseToggle = () => {
        const modifierValue = serviceInfo.lineItem?.codeModifier;
        let matchingCarepath = null;

        if (modifierValue == ESTIMATE_HISTORY_TYPES.rental) {
            matchingCarepath = relatedCarePathsData[ESTIMATE_HISTORY_TYPES.purchase];
        } else {
            matchingCarepath = relatedCarePathsData[ESTIMATE_HISTORY_TYPES.rental];
        }

        setCurrentSelectedServiceForDisplay(matchingCarepath);

        if (getRentalPurchaseEstimateFromContext(matchingCarepath)) {
            return;
        }
        getAlternativeEstimate(matchingCarepath);
    };

    /**
     * Function to get rental/purchase estimates from estimateHistory.
     * @param {*} matchingCarepath selected service details
     * @returns true -> if matching estimates exist in estimateHistory react context, else returns false
     */
    const getRentalPurchaseEstimateFromContext = (matchingCarepath) => {
        let singleEstimateFromHistory = null;
        let multipleEstimateFromHistory = null;

        //Check if we already have the estimates in react context
        if (matchingCarepath?.lineItem?.codeModifier == ESTIMATE_HISTORY_TYPES.rental) {
            multipleEstimateFromHistory =
                estimatesHistory[ESTIMATE_HISTORY_TYPES.rental].multipleEstimate;
            singleEstimateFromHistory =
                estimatesHistory[ESTIMATE_HISTORY_TYPES.rental].singleEstimate;
        } else {
            multipleEstimateFromHistory =
                estimatesHistory[ESTIMATE_HISTORY_TYPES.purchase].multipleEstimate;
            singleEstimateFromHistory =
                estimatesHistory[ESTIMATE_HISTORY_TYPES.purchase].singleEstimate;
        }

        //Estimates already exist in context use them to set the estimate & single estimate response
        if (multipleEstimateFromHistory != null) {
            setEstimateResponse(multipleEstimateFromHistory);
            if (gacOnly || oon) {
                if (singleEstimateFromHistory !== null) {
                    setSingleEstimateResponse(singleEstimateFromHistory);
                    return true;
                } else {
                    return false;
                }
            }
            const checkIfProvidersMatch =
                singleEstimateFromHistory?.claims[0]?.procedures[0]?.provider?.identifiers?.npi ==
                provider?.locAndPractRole[0]?.identifiers?.nationalProviderId;
            if (singleEstimateFromHistory == null || !checkIfProvidersMatch) {
                const singleEstimate = multipleEstimateFromHistory.scenarios?.filter((scenario) => {
                    if (provider) {
                        return (
                            scenario.claims[0].procedures[0].provider?.identifiers?.npi ==
                            provider.locAndPractRole[0]?.identifiers?.nationalProviderId
                        );
                    } else {
                        return false;
                    }
                });
                setSingleEstimateResponse(singleEstimate[0]);
            } else {
                setSingleEstimateResponse(singleEstimateFromHistory);
            }
            return true;
        }

        return false;
    };

    /**
     * Function to get preventive/diagnostic estimate.
     * @param {*} matchingCarepath selected service carepath details
     */
    const getAlternativeEstimate = (matchingCarepath) => {
        let relatedFormValues = formValues;
        relatedFormValues.selectedService.value = matchingCarepath;

        if (selectedProvider?.value || allProviders || oon || gacOnly) {
            if (oon) {
                getSingleEstimate([], oon, false, matchingCarepath);
            } else if (gacOnly) {
                getSingleEstimate([], oon, gacOnly, matchingCarepath);
            } else {
                getEstimates(allProvidersPool, matchingCarepath);
            }
        } else {
            getEstimates(allProvidersPool, matchingCarepath);
        }
    };

    /**
     * Function to handle preventive/diagnostic estimate toggle.
     */
    const handlePreventiveDiagnosticToggle = () => {
        nextIsPreventiveState = !isPreventive;
        setIsPreventive();
        if (getPreventiveDiagnosticEstimateFromContext()) {
            return;
        }
        getPreventiveEstimate();
    };

    /**
     * Function to get preventive/diagnostic estimates from estimateHistory.
     * @returns true -> if matching estimates exist in estimateHistory react context, else returns false
     */
    const getPreventiveDiagnosticEstimateFromContext = () => {
        let singleEstimateFromHistory = null;
        let multipleEstimateFromHistory = null;
        if (nextIsPreventiveState) {
            multipleEstimateFromHistory =
                estimatesHistory[ESTIMATE_HISTORY_TYPES.preventive].multipleEstimate;
            singleEstimateFromHistory =
                estimatesHistory[ESTIMATE_HISTORY_TYPES.preventive].singleEstimate;
        } else {
            multipleEstimateFromHistory =
                estimatesHistory[ESTIMATE_HISTORY_TYPES.diagnostic].multipleEstimate;
            singleEstimateFromHistory =
                estimatesHistory[ESTIMATE_HISTORY_TYPES.diagnostic].singleEstimate;
        }

        if (multipleEstimateFromHistory != null) {
            setEstimateResponse(multipleEstimateFromHistory, nextIsPreventiveState);
            if (gacOnly || oon) {
                if (singleEstimateFromHistory !== null) {
                    setSingleEstimateResponse(singleEstimateFromHistory, nextIsPreventiveState);
                    return true;
                } else {
                    return false;
                }
            }
            const checkIfProvidersMatch =
                singleEstimateFromHistory?.claims[0]?.procedures[0]?.provider?.identifiers?.npi ==
                provider?.locAndPractRole[0]?.identifiers?.nationalProviderId;
            if (singleEstimateFromHistory == null || !checkIfProvidersMatch) {
                const singleEstimate = multipleEstimateFromHistory.scenarios?.filter((scenario) => {
                    if (provider) {
                        return (
                            scenario.claims[0].procedures[0].provider?.identifiers?.npi ==
                            provider.locAndPractRole[0]?.identifiers?.nationalProviderId
                        );
                    } else {
                        return false;
                    }
                });
                setSingleEstimateResponse(singleEstimate[0], nextIsPreventiveState);
            } else {
                setSingleEstimateResponse(singleEstimateFromHistory, nextIsPreventiveState);
            }
            return true;
        }

        return false;
    };

    /**
     * Function to get preventive estimate.
     */
    const getPreventiveEstimate = () => {
        if (selectedProvider?.value || allProviders || oon || gacOnly) {
            if (oon) {
                getSingleEstimate([], oon);
            } else if (gacOnly) {
                getSingleEstimate([], oon, gacOnly);
            } else {
                getEstimates(allProvidersPool);
            }
        } else {
            getEstimates();
        }
    };

    /**
     * Function to get multiple estimates and store the result in context.
     * @param {*} providerData selected provider
     * @param {*} relatedCarePath selected service carepath details
     */
    const getEstimates = async (providerData = allProvidersPool, relatedCarePath = null) => {
        let relatedFormValues = formValues;
        if (relatedCarePath) {
            relatedFormValues.selectedService.value = relatedCarePath;
        }
        setLoading(true);

        await getPrice(
            relatedCarePath ? relatedFormValues : formValues,
            sourceSystem,
            providerData,
            false,
            memberDetails,
            planBenefits,
            getIncrementedCorId(),
            false,
            hasPreventive ? nextIsPreventiveState : false,
            relatedCarePath,
            0
        )
            .then((res) => {
                if (res instanceof Error) {
                    handleChangeCustom(null, 'selectedService');
                    handleChangeCustom(null, 'selectedProvider');
                    setLoading(false);
                    history.push(ERROR_ROUTE);
                    return;
                }
                setEstimateResponse(res, nextIsPreventiveState);

                if (selectedProvider && selectedProvider.value) {
                    setProvider(selectedProvider.value);
                    const singleEstimate = res?.scenarios?.filter((scenario) => {
                        if (selectedProvider && selectedProvider.value) {
                            return (
                                scenario.claims[0].procedures[0].provider?.identifiers?.npi ==
                                selectedProvider.value.locAndPractRole[0]?.identifiers
                                    ?.nationalProviderId
                            );
                        } else {
                            return false;
                        }
                    });
                    setSingleEstimateResponse(singleEstimate[0], nextIsPreventiveState);
                } else {
                    if (provider) {
                        const singleEstimate = res?.scenarios?.filter((scenario) => {
                            if (provider) {
                                return (
                                    scenario.claims[0].procedures[0].provider?.identifiers?.npi ==
                                    provider.locAndPractRole[0]?.identifiers?.nationalProviderId
                                );
                            } else {
                                return false;
                            }
                        });
                        setSingleEstimateResponse(singleEstimate[0], nextIsPreventiveState);
                    }
                    const procedureCode = selectedService?.value?.lineItem?.billingCode?.code;
                    if (procedureCode) {
                        setAllProvidersPool(providerData);
                    } else {
                        setAllProvidersPool([]);
                    }
                }

                setCurrentSelectedServiceForDisplay(selectedService.value);
                if (res?.noInnGACPACRates?.hasNoGACPACRates) {
                    setSingleEstimateResponse(res?.scenarios?.[0], nextIsPreventiveState);
                }
                setLoading(false);
            })
            .catch((error) => {
                console.error('Error getting price', error);
                setEstimateResponse(null, nextIsPreventiveState);
                if (selectedProvider && selectedProvider.value) {
                    setProvider(selectedProvider.value);
                }
                setSingleEstimateResponse(null, nextIsPreventiveState);
                const procedureCode = selectedService?.value?.lineItem?.billingCode?.code;
                if (procedureCode) {
                    setAllProvidersPool(providerData);
                } else {
                    setAllProvidersPool([]);
                }
                setCurrentSelectedServiceForDisplay(selectedService.value);
            });
    };

    /**
     * Function to get single estimate and store the result in context.
     * @param {*} providerData selected provider
     * @param {*} oon boolean flag to define if estimate is OON
     * @param {*} gacOnly boolean flag to define if estimate is GAC only
     * @param {*} relatedCarePath selected service carepath details
     */
    const getSingleEstimate = async (
        providerData,
        oon = false,
        gacOnly = false,
        relatedCarePath = null
    ) => {
        let relatedFormValues = formValues;
        if (relatedCarePath) {
            relatedFormValues.selectedService.value = relatedCarePath;
        }
        setLoading(true);

        await getPrice(
            relatedCarePath ? relatedFormValues : formValues,
            sourceSystem,
            providerData,
            oon,
            memberDetails,
            planBenefits,
            getIncrementedCorId(),
            gacOnly,
            hasPreventive ? nextIsPreventiveState : false,
            relatedCarePath,
            0
        )
            .then((res) => {
                if (res instanceof Error) {
                    handleChangeCustom(null, 'selectedService');
                    handleChangeCustom(null, 'selectedProvider');
                    history.push(ERROR_ROUTE);
                    setLoading(false);
                    return;
                }
                setEstimateResponse(res, nextIsPreventiveState);
                setSingleEstimateResponse(res?.scenarios?.[0], nextIsPreventiveState);
                setCurrentSelectedServiceForDisplay(selectedService?.value);
                setLoading(false);
            })
            .catch((error) => {
                console.error('Error getting price', error);
                setEstimateResponse(null, nextIsPreventiveState);
                setSingleEstimateResponse(null, nextIsPreventiveState);
                setCurrentSelectedServiceForDisplay(selectedService?.value);
            });
    };

    /**
     * Function which returns a string informing which estimate user is viewing
     * @returns A string informing which estimate user is viewing
     */
    const getCurrentEstimateTypeText = () => {
        if (toggleType == 'PreventiveAndDiagnostic') {
            return `You're viewing an estimate for
            ${isPreventive ? 'preventive' : 'diagnostic'} care.`;
        } else {
            return `You're viewing an estimate for
            ${
                serviceInfo?.lineItem?.codeModifier == ESTIMATE_HISTORY_TYPES.rental
                    ? 'rental'
                    : 'purchase'
            }.`;
        }
    };

    /**
     * Function which returns a string which informs user to which estimate he can toggle and view estimate for.
     * @returns A string which informs user to which estimate he can toggle and view estimate for.
     */
    const getAvailableEstimateTypeText = () => {
        if (toggleType == 'PreventiveAndDiagnostic') {
            return `See the estimate for
            ${!isPreventive ? 'preventive' : 'diagnostic'} care.`;
        } else {
            return `See the estimate for
            ${
                serviceInfo?.lineItem?.codeModifier == ESTIMATE_HISTORY_TYPES.rental
                    ? 'purchase'
                    : 'rental'
            }.`;
        }
    };

    return (
        <>
            {loading ? (
                displayLoadingIndicator(isEstimateScreen)
            ) : (
                <div overflow="hidden">
                    <Grid container direction={'row'}>
                        <Grid xs={12} item>
                            <Grid container direction={'row'} alignItems={'flex-end'}>
                                <Grid item>
                                    <Typography.H6
                                        style={{
                                            color: '#333333',
                                            fontWeight: 600,
                                            fontSize: '1rem',
                                        }}
                                    >
                                        {getCurrentEstimateTypeText()}
                                    </Typography.H6>
                                </Grid>
                                <Grid item>
                                    <Link underline="hover" noIcon={true} data-testid={toggleType}>
                                        <Typography.H6
                                            style={{
                                                color: '#196ECF',
                                                fontWeight: 600,
                                                fontSize: '1rem',
                                                cursor: 'pointer',
                                            }}
                                            onClick={handleEstimateToggle}
                                            onKeyDown={(e) => {
                                                if (e.key == 'Enter') {
                                                    handleEstimateToggle();
                                                }
                                            }}
                                            tabIndex="0"
                                        >
                                            &nbsp;
                                            {getAvailableEstimateTypeText()}
                                        </Typography.H6>
                                    </Link>
                                </Grid>
                                {toggleType == 'PreventiveAndDiagnostic' && (
                                    <Grid item>
                                        <div
                                            style={{
                                                height: '38px',
                                            }}
                                        >
                                            <IconButton
                                                aria-label="preventiveInfo"
                                                sx={{
                                                    marginTop: '-6px',
                                                    '&.MuiButtonBase-root:focus': {
                                                        outline: 'none !important',
                                                    },
                                                }}
                                                onClick={() => {
                                                    setShowPreventiveModal(true);
                                                }}
                                            >
                                                <InfoOutlinedIcon color="primary" />
                                            </IconButton>
                                        </div>
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                    <GenericModal
                        showModalDialog={showPreventiveModal}
                        modalStyles={modalStyles}
                        modalLabel="PreventiveDialog"
                        setShowModalDialog={setShowPreventiveModal}
                        modalTitle="Preventive and Diagnostic Care"
                        modalBody={preventiveVerbiage()}
                    ></GenericModal>
                </div>
            )}
        </>
    );
}
